import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPostsByLanguage, JournalPost } from "@/lib/journal";
import { SEOLanguage } from "@/data/seoTranslations";
import JournalCard from "@/components/journal/JournalCard";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const JournalPreviewSection: React.FC = () => {
  const { language, t } = useLanguage();
  const [posts, setPosts] = useState<JournalPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const fetchedPosts = await getPostsByLanguage(language as SEOLanguage);
        // Take only the first 3 posts
        setPosts(fetchedPosts.slice(0, 3));
      } catch (error) {
        console.error("Failed to load journal posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [language]);

  if (loading || posts.length === 0) {
    return null;
  }

  const sectionTitle = language === "de" 
    ? "Aus unserem Magazin" 
    : language === "fr" 
      ? "De notre Journal" 
      : "From Our Journal";

  const viewAllText = language === "de" 
    ? "Alle Artikel ansehen" 
    : language === "fr" 
      ? "Voir tous les articles" 
      : "View All Stories";

  const journalLink = language === "en" ? "/journal" : `/${language}/journal`;

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <span className="text-alpine-gold text-sm font-bold uppercase tracking-widest mb-2 block">
              {language === "de" ? "Inspiration" : "Inspiration"}
            </span>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 leading-tight">
              {sectionTitle}
            </h2>
          </div>
          
          <Button variant="outline" asChild className="hidden md:flex group">
            <Link to={journalLink}>
              {viewAllText}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {posts.map((post) => (
            <JournalCard key={post.slug} post={post} />
          ))}
        </div>

        <div className="mt-12 text-center md:hidden">
          <Button variant="outline" asChild className="w-full group">
            <Link to={journalLink}>
              {viewAllText}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default JournalPreviewSection;
